import {
  BlendFunction,
  BloomEffect,
  ChromaticAberrationEffect,
  EdgeDetectionMode,
  EffectComposer,
  EffectPass,
  KernelSize,
  NoiseEffect,
  PredicationMode,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from "postprocessing";
import * as THREE from "three";

export interface PostFXConfig {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
}

export class Postprocessing {
  readonly composer: EffectComposer;
  private bloom: BloomEffect;
  private chroma: ChromaticAberrationEffect;

  constructor({ renderer, scene, camera }: PostFXConfig) {
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType, // HDR-ready buffers for proper bloom
      multisampling: 0, // SMAA handles AA below — multisampling here is wasteful
    });

    this.composer.addPass(new RenderPass(scene, camera));

    this.bloom = new BloomEffect({
      intensity: 0.55,
      kernelSize: KernelSize.LARGE,
      luminanceThreshold: 0.65,
      luminanceSmoothing: 0.2,
      mipmapBlur: true,
      blendFunction: BlendFunction.ADD,
    });

    this.chroma = new ChromaticAberrationEffect({
      offset: new THREE.Vector2(0, 0),
      radialModulation: true,
      modulationOffset: 0.15,
    });

    const vignette = new VignetteEffect({
      offset: 0.32,
      darkness: 0.55,
      blendFunction: BlendFunction.NORMAL,
    });

    // Subtle film grain over everything — sells the "shot on film" feel
    const noise = new NoiseEffect({
      premultiply: true,
      blendFunction: BlendFunction.OVERLAY,
    });
    noise.blendMode.opacity.value = 0.08;

    // ACES tone mapping in postfx so bloom HDR is preserved through the chain
    const toneMapping = new ToneMappingEffect({
      mode: ToneMappingMode.ACES_FILMIC,
      whitePoint: 4.0,
      middleGrey: 0.6,
    });

    const smaa = new SMAAEffect({
      preset: SMAAPreset.HIGH,
      edgeDetectionMode: EdgeDetectionMode.COLOR,
      predicationMode: PredicationMode.DISABLED,
    });

    // Convolution effects (bloom, chromatic aberration, SMAA) cannot share the
    // same EffectPass. Split them into separate passes; non-convolution effects
    // (vignette, noise, toneMapping) ride along with whichever pass needs them.
    this.composer.addPass(new EffectPass(camera, this.bloom));
    this.composer.addPass(new EffectPass(camera, this.chroma, vignette, noise, toneMapping));
    this.composer.addPass(new EffectPass(camera, smaa));
  }

  /** Pump scroll velocity into chromatic aberration: max 0.003 offset on full speed */
  setScrollVelocity(normalizedVelocity: number): void {
    const v = THREE.MathUtils.clamp(Math.abs(normalizedVelocity), 0, 1);
    const offset = v * 0.0035;
    this.chroma.offset?.set(offset, offset * 0.5);
  }

  /** Bloom can be temporarily boosted at scene transitions for "flash" effect */
  pulseBloom(target = 1.4, duration = 600): void {
    const start = performance.now();
    const initial = 0.55;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic + decay back
      const e = t < 0.3 ? t / 0.3 : 1 - (t - 0.3) / 0.7;
      this.bloom.intensity = initial + (target - initial) * e;
      if (t < 1) requestAnimationFrame(tick);
      else this.bloom.intensity = initial;
    };
    requestAnimationFrame(tick);
  }

  render(deltaSeconds: number): void {
    this.composer.render(deltaSeconds);
  }

  setSize(w: number, h: number): void {
    this.composer.setSize(w, h);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
